import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ColDef, GridApi, GridOptions } from 'ag-grid-enterprise';
import { CellValueChangedEvent, GridReadyEvent } from 'ag-grid-community';
import { LicenseManager } from 'ag-grid-enterprise';
import { Subscription } from 'rxjs';
import { MessageService } from 'primeng/api';
import { environment } from 'src/environments/environment';
import { ContentIssue, FeedbackService } from 'src/app/services/feedback.service';
import { ThemeService } from 'src/app/services/theme.service';

@Component({
  selector: 'app-content-issues',
  templateUrl: './content-issues.component.html',
  styleUrls: ['./content-issues.component.scss'],
})
export class ContentIssuesComponent implements OnInit, OnDestroy {
  licence: any = LicenseManager.setLicenseKey(environment.agGridLicense);

  rowData: ContentIssue[] = [];
  filteredRowData: ContentIssue[] = [];
  isLoading = false;
  rowTransitions = new Map<string, string[]>();

  currentWeekStart: Date = this.getMonday(new Date());
  selectedSource = 'Content Issues';
  selectedStatus = 'All';

  sourceOptions = ['All', 'Content Issues', 'Art', 'Backend', 'QA', 'Other'];
  statusOptions = ['All', 'To Do', 'In Progress', 'Code Review', 'User Testing', 'Approved', 'Closed'];

  copiedKey: string | null = null;
  reportIssueVisible = false;

  columnDefs: ColDef[] = [
    {
      headerName: 'Ticket',
      field: 'key',
      width: 150,
      sortable: true,
      cellRenderer: (params: any) => {
        if (!params.data?.url) return params.value ?? '';
        return `
          <div style="display:flex;align-items:center;gap:6px;height:100%;">
            <a href="${params.data.url}" target="_blank" rel="noopener noreferrer">${params.value}</a>
            <button class="copy-link-btn" data-key="${params.data.key}" data-url="${params.data.url}"
              title="Copy link" style="background:none;border:none;cursor:pointer;padding:2px 4px;opacity:0.6;flex-shrink:0;">
              <i class="pi pi-copy" style="font-size:0.75rem;"></i>
            </button>
          </div>`;
      },
      onCellClicked: (params: any) => {
        const btn = (params.event?.target as HTMLElement)?.closest('.copy-link-btn');
        if (btn) {
          const url = btn.getAttribute('data-url');
          const key = btn.getAttribute('data-key');
          if (url) this.copyTicketUrl(url, key);
        }
      },
    },
    {
      headerName: 'CMS Issue Description',
      field: 'summary',
      flex: 3,
      sortable: true,
      filter: 'agTextColumnFilter',
      wrapText: true,
      autoHeight: true,
    },
    {
      headerName: 'Priority',
      field: 'priority',
      width: 110,
      sortable: true,
      filter: 'agSetColumnFilter',
      cellClass: (params: any) => this.getPriorityClass(params.value),
    },
    {
      headerName: 'Reporter',
      field: 'reporter',
      width: 160,
      sortable: true,
      filter: 'agTextColumnFilter',
    },
    {
      headerName: 'Status',
      field: 'status',
      width: 150,
      sortable: true,
      filter: 'agSetColumnFilter',
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: (params: any) => {
        const current = params.data?.status ?? '';
        const transitions = this.rowTransitions.get(params.data?.key) ?? [];
        // Prepend current status so it is pre-selected; exclude it from transitions to avoid duplicates
        return { values: [current, ...transitions.filter((t) => t !== current)] };
      },
      cellStyle: { cursor: 'pointer', padding: '0 8px' },
      cellRenderer: (params: any) => `
        <div style="display:flex;align-items:center;justify-content:space-between;height:100%;gap:6px;">
          <span>${params.value ?? ''}</span>
          <i class="pi pi-chevron-down" style="font-size:0.65rem;opacity:0.5;flex-shrink:0;"></i>
        </div>`,
    },
    {
      headerName: 'Sprint',
      field: 'sprint',
      width: 160,
      sortable: true,
      filter: 'agTextColumnFilter',
      valueFormatter: (params: any) => params.value ?? 'Backlog',
    },
    {
      headerName: 'Assignee',
      field: 'assignee',
      width: 180,
      sortable: true,
      filter: 'agTextColumnFilter',
    },
  ];

  defaultColDef: ColDef = {
    resizable: true,
    suppressMovable: false,
  };

  gridOptions: GridOptions = {
    pagination: false,
    suppressRowClickSelection: true,
    singleClickEdit: true,
    stopEditingWhenCellsLoseFocus: true,
    rowHeight: 40,
    overlayNoRowsTemplate: '<span class="no-rows-overlay">No tickets found for this week</span>',
  };

  agGridTheme = 'ag-theme-alpine-dark';
  private themeSub: Subscription;
  private gridApi: GridApi;
  private isReverting = false;

  constructor(
    private readonly feedbackService: FeedbackService,
    private readonly themeService: ThemeService,
    private readonly messageService: MessageService,
    private readonly route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.themeSub = this.themeService.isDark$.subscribe((isDark) => {
      this.agGridTheme = isDark ? 'ag-theme-alpine-dark' : 'ag-theme-alpine';
    });
    const sourceParam = this.route.snapshot.queryParamMap.get('source');
    if (sourceParam && this.sourceOptions.includes(sourceParam)) {
      this.selectedSource = sourceParam;
    }
    this.loadIssues();
  }

  ngOnDestroy(): void {
    this.themeSub?.unsubscribe();
  }

  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;
  }

  async loadIssues(): Promise<void> {
    this.isLoading = true;
    this.rowTransitions.clear();
    try {
      this.rowData = await this.feedbackService.getIssuesByWeek(this.selectedSource, this.currentWeekStart);
      this.applyStatusFilter();
      await Promise.all(
        this.rowData.map((row) =>
          this.feedbackService.getIssueTransitions(row.key).then((t) => this.rowTransitions.set(row.key, t)).catch(() => {}),
        ),
      );
    } catch {
      this.rowData = [];
      this.filteredRowData = [];
    } finally {
      this.isLoading = false;
    }
  }

  prevWeek(): void {
    const d = new Date(this.currentWeekStart);
    d.setDate(d.getDate() - 7);
    this.currentWeekStart = d;
    this.loadIssues();
  }

  nextWeek(): void {
    const d = new Date(this.currentWeekStart);
    d.setDate(d.getDate() + 7);
    this.currentWeekStart = d;
    this.loadIssues();
  }

  onSourceChange(): void {
    this.loadIssues();
  }

  onStatusChange(): void {
    this.applyStatusFilter();
  }

  private applyStatusFilter(): void {
    this.filteredRowData = this.selectedStatus === 'All'
      ? [...this.rowData]
      : this.rowData.filter((r) => r.status === this.selectedStatus);
  }

  get isCurrentWeek(): boolean {
    const thisMonday = this.getMonday(new Date());
    return this.currentWeekStart.toDateString() === thisMonday.toDateString();
  }

  get weekLabel(): string {
    const end = new Date(this.currentWeekStart);
    end.setDate(end.getDate() + 4);
    const fmt = (d: Date) =>
      d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const year = end.getFullYear();
    return `${fmt(this.currentWeekStart)} – ${fmt(end)}, ${year}`;
  }

  async onStatusChanged(event: CellValueChangedEvent): Promise<void> {
    if (event.column.getColId() !== 'status' || event.newValue === event.oldValue || this.isReverting) return;
    const key = event.data.key;
    try {
      await this.feedbackService.updateIssueStatus(key, event.newValue);
      // Keep rowData in sync so event.oldValue is correct for any subsequent edit
      const row = this.rowData.find((r) => r.key === key);
      if (row) row.status = event.newValue;
      // Refresh transitions for this row so next click shows valid options from new status
      this.feedbackService.getIssueTransitions(key).then((t) => this.rowTransitions.set(key, t)).catch(() => {});
      this.messageService.add({
        severity: 'success',
        summary: `${key} updated`,
        detail: `Status → ${event.newValue}`,
        life: 3000,
      });
    } catch (err: any) {
      // Revert cell value on failure — guard prevents the revert from re-triggering this handler
      this.isReverting = true;
      event.node.setDataValue('status', event.oldValue);
      this.isReverting = false;
      this.messageService.add({
        severity: 'error',
        summary: 'Status update failed',
        detail: err?.error?.message || err?.message || 'Transition not available',
        life: 5000,
      });
    }
  }

  async copyTicketUrl(url: string, key: string | null): Promise<void> {
    try {
      await navigator.clipboard.writeText(url);
      this.copiedKey = key;
      this.messageService.add({ severity: 'success', summary: 'Link copied', detail: key ?? url, life: 2000 });
      setTimeout(() => (this.copiedKey = null), 2000);
    } catch {
      // clipboard denied — silently ignore
    }
  }

  openReportIssue(): void {
    this.reportIssueVisible = true;
  }

  private getMonday(date: Date): Date {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const day = d.getDay();
    d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
    return d;
  }

  private getPriorityClass(priority: string): string {
    const map: Record<string, string> = {
      Highest: 'priority-critical',
      Critical: 'priority-critical',
      High: 'priority-high',
      Medium: 'priority-medium',
      Low: 'priority-low',
    };
    return map[priority] ?? '';
  }
}
